/**
 * MyFamilyTreeCareHome — landing page for myfamilytree.care.
 *
 * Preview modes:
 *   /?site=myfamilytreecare        → renders this page inside the SPA
 *   https://myfamilytree.care      → auto-detected via HomeRouter
 *
 * Focus: heritage-archive / memorial vertical of the platform. Same
 * shared backend as Social Pulse + IFT — content differentiation is
 * purely presentational (green + gold + cream palette, memorial-first
 * copy, "Celebrate a Life" hero CTA, cemeteries + funeral services
 * surfaced up-front).
 *
 * All data flows through the standard Social Pulse APIs with the
 * `X-Site-Source: myfamilytreecare` header applied.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Heart, Flower2, Users, ArrowRight,
  Landmark, ScrollText, BookOpen, Sparkles,
} from "lucide-react";
import HeritageResourcesSection from "@/components/HeritageResourcesSection";
import CousinDiscoveryFeed from "@/components/CousinDiscoveryFeed";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;

// Heritage-green + gold + cream palette — calm and reverent to fit
// the memorial-vertical brand. Independent of the IFT palette so
// changes here don't ripple into indianfamilytree.com.
const P = {
  bg:        "#F7F4EC",         // cream
  ink:       "#1F2A24",         // moss-black
  inkSoft:   "#4C5A52",
  green:     "#0E7C55",         // heritage green
  greenSoft: "#C6EAD6",
  gold:      "#C08A2E",
  goldSoft:  "#F5E6C4",
};


export default function MyFamilyTreeCareHome() {
  const [memorials, setMemorials] = useState([]);
  const [stats, setStats] = useState({
    lineages: 0, tributes: 0, memorials: 0, obituaries: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "MyFamilyTree.care — Honour Loved Ones, Preserve Memories";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Public memorials — same collection Social Pulse serves, but
        // filtered client-side so a first-page render feels curated.
        const res = await axios.get(`${API}/api/memorials?limit=6`, {
          headers: { "X-Site-Source": "myfamilytreecare" },
        });
        if (cancelled) return;
        const list = res.data.items || res.data.memorials || res.data || [];
        setMemorials(Array.isArray(list) ? list.slice(0, 6) : []);
      } catch { /* soft-fail — hide the strip */ }
      // Live network stats — same endpoint the marketing landing uses.
      try {
        const res2 = await axios.get(`${API}/api/stats/network?site_source=myfamilytreecare`);
        if (cancelled) return;
        setStats({
          lineages:   res2.data?.lineages   ?? 0,
          tributes:   res2.data?.tributes   ?? 0,
          memorials:  res2.data?.memorials  ?? 0,
          obituaries: res2.data?.obituaries ?? 0,
        });
      } catch { /* soft-fail — floors handle it */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const statCards = useMemo(() => ([
    { k: "lineages",   v: stats.lineages,   label: "Family lineages" },
    { k: "memorials",  v: stats.memorials,  label: "Memorials & tributes" },
    { k: "obituaries", v: stats.obituaries, label: "Obituaries preserved" },
    { k: "tributes",   v: stats.tributes,   label: "Tributes & candles" },
  ]), [stats]);

  return (
    <div style={{ background: P.bg, color: P.ink }} data-testid="mftcare-home">
      <SeoHead
        section="celebrate"
        title="MyFamilyTree.care — Honour Loved Ones, Preserve Memories"
        description="Digital memorials, cemetery locators, funeral service directories, and family trees — for families across India and the diaspora to honour their loved ones."
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${P.greenSoft} 0%, #ffffff 55%, ${P.goldSoft} 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider rounded-full px-3 py-1 mb-4"
              style={{ background: "#fff", color: P.green, border: `1px solid ${P.greenSoft}` }}
              data-testid="mftcare-hero-kicker"
            >
              <Heart className="w-3 h-3" fill={P.green} strokeWidth={0} />
              A place of remembrance
            </div>
            <h1
              className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mb-4"
              style={{ color: P.ink }}
              data-testid="mftcare-hero-h1"
            >
              Honour their story. <span style={{ color: P.green }}>Keep memory alive.</span>
            </h1>
            <p className="text-base sm:text-lg mb-6" style={{ color: P.inkSoft }}>
              Free digital memorials, family trees, cemetery locators and funeral-service directories — thoughtfully designed for Indian families and the global diaspora who want to honour loved ones for generations to come.
            </p>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/celebrate/submit"
                className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
                style={{ background: P.green }}
                data-testid="mftcare-cta-create-memorial"
              >
                <Flower2 className="w-4 h-4" /> Create a free memorial
              </Link>
              <Link
                to="/family-trees/new"
                className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold border"
                style={{ borderColor: P.green, color: P.green, background: "#fff" }}
                data-testid="mftcare-cta-start-tree"
              >
                <Users className="w-4 h-4" /> Start a family tree
              </Link>
              <Link
                to="/obituaries/hub"
                className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold"
                style={{ color: P.ink }}
                data-testid="mftcare-cta-obituaries"
              >
                <ScrollText className="w-4 h-4" /> Browse obituaries
              </Link>
            </div>
          </div>

          {/* Stats card */}
          <div
            className="rounded-2xl bg-white shadow-sm p-5 border"
            style={{ borderColor: P.greenSoft }}
            data-testid="mftcare-hero-stats"
          >
            <div className="text-[10px] font-mono uppercase tracking-wider mb-3" style={{ color: P.green }}>
              A growing archive of remembrance
            </div>
            <div className="grid grid-cols-2 gap-3">
              {statCards.map((s) => (
                <div
                  key={s.k}
                  className="rounded-xl border p-3"
                  style={{ borderColor: P.greenSoft, background: P.bg }}
                  data-testid={`mftcare-stat-${s.k}`}
                >
                  <div className="font-display text-2xl font-bold" style={{ color: P.green }}>
                    {loading ? "…" : (s.v?.toLocaleString?.() || s.v || "—")}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: P.inkSoft }}>
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Heritage resources — cemeteries, funeral services, articles */}
      <HeritageResourcesSection palette={P} siteScope="myfamilytreecare" />

      {/* Recent memorials strip */}
      {memorials.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-4 h-4" style={{ color: P.green }} />
            <h2 className="font-display text-2xl">Recently honoured</h2>
            <Link
              to="/celebrate"
              className="text-xs ml-auto opacity-70 hover:opacity-100 inline-flex items-center gap-1"
              data-testid="mftcare-see-all-memorials"
            >
              See all memorials <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {memorials.map((m) => (
              <Link
                key={m.id || m.key || m.slug}
                to={`/celebrate/${m.slug || m.key || m.id}`}
                className="rounded-2xl border bg-white overflow-hidden hover:shadow-md transition-shadow"
                style={{ borderColor: P.greenSoft }}
                data-testid={`mftcare-memorial-card-${m.slug || m.key || m.id}`}
              >
                {(m.hero_photo || m.photo || m.memorial_card_url) && (
                  <div className="aspect-[4/3] bg-black/5">
                    <img
                      src={m.memorial_card_url || m.hero_photo || m.photo}
                      alt={m.full_name || m.name || "Memorial"}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-4">
                  <div className="font-display text-base leading-snug">
                    {m.full_name || m.name || "In loving memory"}
                  </div>
                  <div className="text-xs mt-1" style={{ color: P.inkSoft }}>
                    {m.birth_year || m.birth_date_str || ""}
                    {(m.birth_year || m.birth_date_str) && (m.death_year || m.death_date_str) ? " — " : ""}
                    {m.death_year || m.death_date_str || ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Cousin discovery — cross-site matches. */}
      <CousinDiscoveryFeed palette={P} siteScope="myfamilytreecare" />

      {/* Closing CTA band */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div
          className="rounded-3xl p-8 md:p-10 text-center border"
          style={{
            borderColor: P.greenSoft,
            background: `linear-gradient(135deg, ${P.greenSoft} 0%, #ffffff 60%, ${P.goldSoft} 100%)`,
          }}
        >
          <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: P.green }} />
          <h3 className="font-display text-2xl md:text-3xl mb-2" style={{ color: P.ink }}>
            Every life deserves a story worth remembering.
          </h3>
          <p className="text-sm md:text-base mb-5" style={{ color: P.inkSoft }}>
            Begin their legacy today — free forever, private by default, and always in your family's hands.
          </p>
          <Link
            to="/celebrate/submit"
            className="inline-flex items-center gap-1.5 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm"
            style={{ background: P.green }}
            data-testid="mftcare-cta-closing"
          >
            <BookOpen className="w-4 h-4" /> Create a free memorial page
          </Link>
        </div>
      </section>
    </div>
  );
}
